// Navigation-experience Sentry DSN
export const SENTRY_DSN =
	'https://b8c4ebbdd822beb92561fea2a46d2146@o55978.ingest.us.sentry.io/4510463033081856';

export const SENTRY_TEAM_NAME = 'afe-navigation-experience';

/**
 * Transport-level failures (no usable HTTP response) that aren't actionable in the FE.
 *
 * Spread into the client's `ignoreErrors`, which Sentry tests against `event.message`, the
 * exception `value` and `${type}: ${value}`. `beforeSend` re-checks them against the same strings
 * plus the wrapped `originalError` of BFF errors.
 *
 * These patterns must NOT match errors that carry an HTTP status: those are classified by
 * status in `classifyHttpStatus` so that FE-caused 4xx responses (400/404/422) are still reported.
 */
export const SUPPRESSED_BACKEND_ERROR_PATTERNS: RegExp[] = [
	// Browser-level network failures. Newer browsers append the request host, e.g.
	// `Failed to fetch (rovo-extension-web.atlassian.com)`, so allow an optional `(host)` suffix
	// while staying anchored (so e.g. "Failed to fetch universal create menu items" won't match).
	/Failed to fetch(?: \([^)]*\))?$/i, // Chromium
	/NetworkError when attempting to fetch/i, // Firefox
	/Load failed(?: \([^)]*\))?$/i, // Safari
	// Aborted requests. Anchored to the error type (`AbortError: ...`) so FE errors that merely
	// mention "AbortError" in their message are still reported.
	/^AbortError\b/i,
	/The user aborted a request/i,
	// `fetchJSON` failures without a status code (e.g. `fetch() returned no response`).
	/^Failed to fetch data:(?!\s*\d{3}\b)/i,
	/^RelayNetwork: No data returned for operation/i,
	/^FetchError:/i,
	/^Error getting available products by site/i,
];

/**
 * Extracts an HTTP status code from an error message of the shape
 * `Failed to fetch data: <status> <statusText>` (produced by `fetchJSON`).
 * Returns `undefined` when no status code can be found.
 */
export function extractHttpStatusFromMessage(message?: string): number | undefined {
	if (!message) {
		return undefined;
	}
	const match = /^Failed to fetch data:\s*(\d{3})\b/i.exec(message);
	return match ? Number(match[1]) : undefined;
}

/**
 * Fraction of rate-limited (HTTP 429) events that are still reported. 429s are mostly BE noise,
 * but a spike can also mean the FE is refetching in a loop, so keep a small sample for visibility.
 */
export const RATE_LIMITED_SAMPLE_RATE = 0.01;

/**
 * How an HTTP status should be reported:
 * - `suppress`: non-actionable in the FE (401/403 auth, 5xx server failures).
 * - `sample`: report only `RATE_LIMITED_SAMPLE_RATE` of events (429).
 * - `report`: always report. Includes 400/404/422 etc., which are often caused by the FE
 *   (bad URL / payload), and any status that couldn't be determined.
 */
export type HttpStatusClassification = 'suppress' | 'sample' | 'report';

export function classifyHttpStatus(status: number | undefined): HttpStatusClassification {
	if (status === undefined) {
		return 'report';
	}
	if (status === 429) {
		return 'sample';
	}
	if (status === 401 || status === 403 || (status >= 500 && status <= 599)) {
		return 'suppress';
	}
	return 'report';
}

export const DEFAULT_SENTRY_CONFIG: {
	attachStacktrace: boolean;
	ignoreErrors: RegExp[];
	ignoreUrls: RegExp[];
	whitelistUrls: RegExp[];
} = {
	whitelistUrls: [/atl-paas.net/, /localhost/],
	ignoreErrors: [/^SentryIgnore:/],
	ignoreUrls: [/extensions\//i, /^chrome:\/\//i],
	attachStacktrace: true,
};

export const SENTRY_PRODUCT = 'navigation';
